import * as React from "react";

import { setLogoutHandler, tokenStore } from "@/api/client";
import { auth } from "@/api/endpoints";
import type { Permission, StaffMe } from "@/api/types";

interface AuthContextValue {
  me: StaffMe | null;
  loading: boolean;
  /** True once a token exists AND /auth/me has confirmed it. */
  isAuthenticated: boolean;
  can: (...permissions: Permission[]) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
  refreshMe: () => Promise<void>;
  signIn: (access: string, refresh: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = React.useState<StaffMe | null>(null);
  const [loading, setLoading] = React.useState(true);

  const clear = React.useCallback(() => {
    tokenStore.clear();
    setMe(null);
  }, []);

  // The client calls this when a refresh attempt fails.
  React.useEffect(() => {
    setLogoutHandler(() => {
      setMe(null);
    });
  }, []);

  const refreshMe = React.useCallback(async () => {
    if (!tokenStore.access) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      setMe(await auth.me());
    } catch {
      clear();
    } finally {
      setLoading(false);
    }
  }, [clear]);

  React.useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  const signIn = React.useCallback(
    async (access: string, refresh: string) => {
      tokenStore.set(access, refresh);
      setLoading(true);
      await refreshMe();
    },
    [refreshMe],
  );

  const signOut = React.useCallback(async () => {
    const refresh = tokenStore.refresh;
    if (refresh) {
      // Best-effort server-side revocation; the local session ends either way.
      try {
        await auth.logout(refresh);
      } catch {
        /* ignore */
      }
    }
    clear();
  }, [clear]);

  const permissions = React.useMemo(
    () => new Set<Permission>(me?.permissions ?? []),
    [me],
  );

  const value = React.useMemo<AuthContextValue>(
    () => ({
      me,
      loading,
      isAuthenticated: me !== null,
      can: (...required: Permission[]) => required.every((p) => permissions.has(p)),
      canAny: (...required: Permission[]) => required.some((p) => permissions.has(p)),
      refreshMe,
      signIn,
      signOut,
    }),
    [me, loading, permissions, refreshMe, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
